import type { RespondentAnswer, RespondentConversationState, RespondentField, RespondentMessage } from '../../lib/multimodal-contract';

/** A stable public question and its chronological conversation segment. */
export interface ConversationChapter {
  id: string;
  field: RespondentField | null;
  answer?: RespondentAnswer;
  current: boolean;
  messages: RespondentMessage[];
}

/** Keep observed questions while projecting current answers without reordering ordinary dialogue. */
export function buildConversationChapters(state: RespondentConversationState, retained: RespondentField[] = [], authoredOrder?: readonly string[]): ConversationChapter[] {
  const hidden = new Set(state.hiddenFieldIds);
  const hiddenQuestions = new Set(retained.filter(field => hidden.has(field.fieldId)).map(field => field.questionMessageId ?? `question:${field.fieldId}`));
  const messages = state.messages.filter(message => !(message.fieldId && hidden.has(message.fieldId))
    && !(message.role === 'assistant' && hiddenQuestions.has(message.id)));
  const fields = new Map(retained.map(field => [field.fieldId, field]));
  const order = [...new Set([...retained.map(field => field.fieldId), ...state.answers.map(answer => answer.fieldId), ...(state.activeField ? [state.activeField.fieldId] : [])])].filter(id => !hidden.has(id));
  if (authoredOrder) {
    const positions = new Map(authoredOrder.map((id, index) => [id, index]));
    order.sort((left, right) => (positions.get(left) ?? authoredOrder.length) - (positions.get(right) ?? authoredOrder.length));
  }
  for (const answer of state.answers) if (answer.field) fields.set(answer.fieldId, answer.field);
  if (state.activeField) fields.set(state.activeField.fieldId, state.activeField);
  const chapters: ConversationChapter[] = order.flatMap(id => fields.has(id) ? [fields.get(id)!] : []).map(field => ({ id: field.fieldId, field,
    answer: state.answers.find(answer => answer.fieldId === field.fieldId), current: state.activeField?.fieldId === field.fieldId, messages: [] as RespondentMessage[] }));
  const indices = new Map(chapters.map((chapter, index) => [chapter.id, index]));
  const questions = new Map(chapters.map((chapter, index) => [chapter.field?.questionMessageId ?? `question:${chapter.id}`, index]));
  if (!chapters.length) return [{ id: 'history', field: null, current: false, messages: messages.filter(message => message.modality !== 'speech') }];
  let owner = 0;
  for (const message of messages) {
    if (message.modality === 'speech') continue;
    const questionIndex = message.role === 'assistant' ? questions.get(message.id) : undefined;
    const index = questionIndex ?? (message.fieldId ? indices.get(message.fieldId) : undefined);
    if (index !== undefined) owner = Math.max(owner, index);
    const chapter = index === undefined ? undefined : chapters[index];
    const authored = questionIndex !== undefined;
    const acceptedReceipt = chapter?.answer && message.role === 'user' && message.modality === 'control';
    if (!authored && !acceptedReceipt) chapters[owner]?.messages.push(message);
  }
  return chapters;
}

/** Choose the chapter that owns the activation line, including naturally tall chapters. */
export function chapterIndexAtPosition(offsets: number[], scrollTop: number, viewportHeight: number): number {
  const activation = scrollTop + Math.min(2, viewportHeight * 0.01);
  let index = 0;
  for (let candidate = 1; candidate < offsets.length; candidate++) {
    if (offsets[candidate]! > activation) break;
    index = candidate;
  }
  return index;
}
